const DISPLAY_FALLBACK = 'N/A';

const PRIORITY_MESSAGE_KEYS = ['reason', 'message', 'detail', 'error', 'log_message'] as const;

const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const parseJsonString = (value: string): unknown => {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
    return value;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return value;
  }
};

const normalizeEmailPipelineLogValue = (value: unknown): unknown =>
  typeof value === 'string' ? parseJsonString(value) : value;

export const getEmailPipelineLogValue = (value: unknown): unknown => normalizeEmailPipelineLogValue(value);

const stringifyEmailPipelineLogMessage = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    const normalizedValue = normalizeEmailPipelineLogValue(value);
    if (normalizedValue !== value) {
      return stringifyEmailPipelineLogMessage(normalizedValue);
    }

    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyEmailPipelineLogMessage(item))
      .filter(Boolean)
      .join(', ');
  }

  if (isRecord(value)) {
    for (const key of PRIORITY_MESSAGE_KEYS) {
      const prioritizedValue = value[key];
      const formattedPrioritizedValue = stringifyEmailPipelineLogMessage(prioritizedValue);
      if (formattedPrioritizedValue) {
        return formattedPrioritizedValue;
      }
    }

    const entries = Object.entries(value)
      .map(([key, entryValue]) => {
        const formattedEntryValue = stringifyEmailPipelineLogMessage(entryValue);
        if (!formattedEntryValue) {
          return '';
        }

        return `${humanizeKey(key)}: ${formattedEntryValue}`;
      })
      .filter(Boolean);

    return entries.join(', ');
  }

  return '';
};

export const formatEmailPipelineLogMessage = (value: unknown): string => {
  const formattedValue = stringifyEmailPipelineLogMessage(value);
  return formattedValue || DISPLAY_FALLBACK;
};

export const isStructuredEmailPipelineLogMessage = (value: unknown): boolean => {
  const normalizedValue = normalizeEmailPipelineLogValue(value);
  return Array.isArray(normalizedValue) || isRecord(normalizedValue);
};

export const formatEmailPipelineLogJson = (value: unknown): string => {
  const normalizedValue = normalizeEmailPipelineLogValue(value);

  if (normalizedValue == null) {
    return DISPLAY_FALLBACK;
  }

  if (typeof normalizedValue === 'string') {
    return normalizedValue.trim() || DISPLAY_FALLBACK;
  }

  if (typeof normalizedValue === 'number' || typeof normalizedValue === 'boolean') {
    return String(normalizedValue);
  }

  try {
    return JSON.stringify(normalizedValue, null, 2) || DISPLAY_FALLBACK;
  } catch {
    return stringifyEmailPipelineLogMessage(normalizedValue) || DISPLAY_FALLBACK;
  }
};
